import { If, Form, FormApi } from 'cdui-js';
import { RadioGroup } from '../../../../components/RadioGroup';
import { lastProcess, nextProcess, pState, setFormApi } from '../../store';
import { LastNextBtns } from '../../components/LastNextBtns';
import { saveCareer } from '../../../../../../api/open-account';
import { OpenFormItem } from '../../components/OpenFormItem';
import { FieldReEditHint } from '../../components/FieldReEditHint';
import { DiFiInput } from '../../../../../../components/DiFi/DiFiInput';
import i18n from '../../../../../../i18n';
import { isOpenProgressReEdit } from '../../reEditMode';

const yesNoOptions = () => [
  { label: i18n.OpenProgress.Common.No, value: 0 },
  { label: i18n.OpenProgress.Common.YesPleaseSpecify, value: 1 },
];

/** 两行输入拼成一个 Desc：`part1,part2`（仅按第一个逗号拆，避免内容含逗号时错位） */
export const joinDescPair = (part1: string, part2: string) => `${part1 || ''},${part2 || ''}`;

export const splitDescPair = (desc: string): [string, string] => {
  const raw = String(desc || '');
  const i = raw.indexOf(',');
  if (i < 0) return [raw, ''];
  return [raw.slice(0, i), raw.slice(i + 1)];
};

const syncSpouseDesc = () => {
  const f = pState.othersForm;
  f.spouseHoldCompanyAccountDesc = joinDescPair(f.spouseHoldName, f.spouseHoldAccountNo);
};

const syncVotingDesc = () => {
  const f = pState.othersForm;
  f.controlOtherClientVoting35Desc = joinDescPair(f.controlVotingAccountName, f.controlVotingAccountNo);
};

const buildOthersPayload = () => {
  const f = pState.othersForm;
  const spouseYes = f.isSpouseHoldCompanyAccount === 1;
  const votingYes = f.isControlOtherClientVoting35 === 1;
  return {
    isSfcLicensed: f.isSfcLicensed,
    sfcLicenseDesc: f.isSfcLicensed === 1 ? f.sfcLicenseDesc || '' : '',
    isRelativeToStaff: f.isRelativeToStaff,
    staffRelativeDesc: f.isRelativeToStaff === 1 ? f.staffRelativeDesc || '' : '',
    isSpouseHoldCompanyAccount: f.isSpouseHoldCompanyAccount,
    spouseHoldCompanyAccountDesc: spouseYes
      ? joinDescPair(f.spouseHoldName, f.spouseHoldAccountNo)
      : '',
    isControlOtherClientVoting35: f.isControlOtherClientVoting35,
    controlOtherClientVoting35Desc: votingYes
      ? joinDescPair(f.controlVotingAccountName, f.controlVotingAccountNo)
      : '',
    progress: 9,
  };
};

export const Others = (props) => {
  let formApi: FormApi = null;
  const O = i18n.OpenProgress.Info.Others;

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-XS">
        {O.Title}
        {props.stepText}
      </div>

      <Form
        data={pState.othersForm}
        rules={{}}
        align="top"
        api={(api) => {
          formApi = api;
          setFormApi(api);
        }}
      >
        {/* 1 SFC 持牌 */}
        <div>
          <div class="color-D">{O.SfcQuestion}</div>
          <RadioGroup
            class="flex gap-16px margin-t-S"
            value={pState.othersForm.isSfcLicensed}
            options={yesNoOptions()}
            change={(e) => {
              pState.othersForm.isSfcLicensed = e;
              if (e !== 1) pState.othersForm.sfcLicenseDesc = '';
            }}
          />
          <FieldReEditHint field="isSfcLicensed" />
          <If when={pState.othersForm.isSfcLicensed === 1}>
            <OpenFormItem label="" field="sfcLicenseDesc" required>
              <DiFiInput
                class="margin-t-S"
                shellClass="f-input-2"
                inputKind="text"
                maxLength={80}
                placeholder={O.SfcPlaceholder}
                value={pState.othersForm.sfcLicenseDesc}
                onUpdateValue={(v) => (pState.othersForm.sfcLicenseDesc = v)}
              />
            </OpenFormItem>
          </If>
        </div>

        {/* 2 亲属关系 */}
        <div class="margin-t-L">
          <div class="color-D">{O.StaffRelativeQuestion}</div>
          <RadioGroup
            class="flex gap-16px margin-t-S"
            value={pState.othersForm.isRelativeToStaff}
            options={yesNoOptions()}
            change={(e) => {
              pState.othersForm.isRelativeToStaff = e;
              if (e !== 1) pState.othersForm.staffRelativeDesc = '';
            }}
          />
          <FieldReEditHint field="isRelativeToStaff" />
          <If when={pState.othersForm.isRelativeToStaff === 1}>
            <OpenFormItem label="" field="staffRelativeDesc" required>
              <DiFiInput
                class="margin-t-S"
                shellClass="f-input-2"
                inputKind="text"
                maxLength={80}
                placeholder={O.StaffRelativePlaceholder}
                value={pState.othersForm.staffRelativeDesc}
                onUpdateValue={(v) => (pState.othersForm.staffRelativeDesc = v)}
              />
            </OpenFormItem>
          </If>
        </div>

        {/* 3 配偶账户：两行 UI → spouseHoldCompanyAccountDesc = name,accountNo */}
        <div class="margin-t-L">
          <div class="color-D">{O.SpouseAccountQuestion}</div>
          <RadioGroup
            class="flex gap-16px margin-t-S"
            value={pState.othersForm.isSpouseHoldCompanyAccount}
            options={yesNoOptions()}
            change={(e) => {
              pState.othersForm.isSpouseHoldCompanyAccount = e;
              if (e !== 1) {
                pState.othersForm.spouseHoldName = '';
                pState.othersForm.spouseHoldAccountNo = '';
                pState.othersForm.spouseHoldCompanyAccountDesc = '';
              }
            }}
          />
          <FieldReEditHint field="isSpouseHoldCompanyAccount" />
          <If when={pState.othersForm.isSpouseHoldCompanyAccount === 1}>
            <div class="flex-column gap-8px margin-t-S">
              <OpenFormItem label="" field="spouseHoldName" required class="open-form-item-compact">
                <DiFiInput
                  shellClass="f-input-2"
                  inputKind="text"
                  maxLength={80}
                  placeholder={O.SpouseNamePlaceholder}
                  value={pState.othersForm.spouseHoldName}
                  onUpdateValue={(v) => {
                    pState.othersForm.spouseHoldName = v;
                    syncSpouseDesc();
                  }}
                />
              </OpenFormItem>
              <OpenFormItem label="" field="spouseHoldAccountNo" required class="open-form-item-compact">
                <DiFiInput
                  shellClass="f-input-2"
                  inputKind="text"
                  maxLength={80}
                  placeholder={O.AccountNoPlaceholder}
                  value={pState.othersForm.spouseHoldAccountNo}
                  onUpdateValue={(v) => {
                    pState.othersForm.spouseHoldAccountNo = v;
                    syncSpouseDesc();
                  }}
                />
              </OpenFormItem>
            </div>
          </If>
        </div>

        {/* 4 投票权：两行 UI → controlOtherClientVoting35Desc = name,accountNo */}
        <div class="margin-t-L">
          <div class="color-D">{O.VotingControlQuestion}</div>
          <RadioGroup
            class="flex gap-16px margin-t-S"
            value={pState.othersForm.isControlOtherClientVoting35}
            options={yesNoOptions()}
            change={(e) => {
              pState.othersForm.isControlOtherClientVoting35 = e;
              if (e !== 1) {
                pState.othersForm.controlVotingAccountName = '';
                pState.othersForm.controlVotingAccountNo = '';
                pState.othersForm.controlOtherClientVoting35Desc = '';
              }
            }}
          />
          <FieldReEditHint field="isControlOtherClientVoting35" />
          <If when={pState.othersForm.isControlOtherClientVoting35 === 1}>
            <div class="flex-column gap-8px margin-t-S">
              <OpenFormItem label="" field="controlVotingAccountName" required class="open-form-item-compact">
                <DiFiInput
                  shellClass="f-input-2"
                  inputKind="text"
                  maxLength={80}
                  placeholder={O.AccountNamePlaceholder}
                  value={pState.othersForm.controlVotingAccountName}
                  onUpdateValue={(v) => {
                    pState.othersForm.controlVotingAccountName = v;
                    syncVotingDesc();
                  }}
                />
              </OpenFormItem>
              <OpenFormItem label="" field="controlVotingAccountNo" required class="open-form-item-compact">
                <DiFiInput
                  shellClass="f-input-2"
                  inputKind="text"
                  maxLength={80}
                  placeholder={O.AccountNoPlaceholder}
                  value={pState.othersForm.controlVotingAccountNo}
                  onUpdateValue={(v) => {
                    pState.othersForm.controlVotingAccountNo = v;
                    syncVotingDesc();
                  }}
                />
              </OpenFormItem>
            </div>
          </If>
        </div>
      </Form>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            const valid = await formApi.validate();
            if (!valid) return;
            await saveCareer(buildOthersPayload());
            nextProcess();
          }}
        />
      </If>
    </div>
  );
};

export { buildOthersPayload };
